import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Btn, Card, Field, Input, TextArea } from "../components/common";
import { PasswordFields, pwReady } from "../components/PasswordFields";

const Wrap = ({ children }) => <div className="min-h-screen bg-[#FAF7F2] px-4 py-10"><Card className="max-w-lg mx-auto space-y-4">{children}</Card></div>;
const Back = () => <Link to="/login" className="font-semibold text-[#C85A32]" data-testid="back-to-login">← Front page / Sign in</Link>;

export function AuthComplete() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  useEffect(() => { api.get("/auth/token-info", { params: { token } }).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const go = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/complete", { token, password: pw }); setDone(data.message); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (done) return <Wrap><CheckCircle2 className="h-12 w-12 text-[#2D6A4F]" /><p className="text-lg font-semibold" data-testid="complete-message">{done}</p><Back /></Wrap>;
  if (!info) return <Wrap>{err ? <><p className="text-[#B91C1C] font-semibold" data-testid="complete-error">{err}</p><Back /></> : <Loader2 className="h-8 w-8 animate-spin" />}</Wrap>;
  const title = { register: "Verify your email", email_change: "Verify your email address", reset: "Reset Your GKK-FMIS Password" }[info.purpose];
  return (
    <Wrap>
      <h1 className="font-serif text-2xl font-bold" data-testid="complete-title">{title}</h1>
      <p className="text-sm">{info.name ? `${info.name} · ` : ""}{info.email}</p>
      {info.needs_password && <PasswordFields pw={pw} setPw={setPw} confirm={confirm} setConfirm={setConfirm} />}
      {err && <p className="rounded-xl bg-[#FEF2F2] text-[#B91C1C] p-3 font-semibold" data-testid="complete-error">{err}</p>}
      <Btn v="green" className="w-full" loading={busy} disabled={busy || (info.needs_password && !pwReady(pw, confirm))} onClick={go} data-testid="complete-submit">
        {info.needs_password ? (info.purpose === "reset" ? "Save new password" : "Verify email and create password") : "Verify my email"}</Btn>
    </Wrap>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/forgot", { email: email.trim(), origin: window.location.origin }); setMsg(data.message); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Wrap>
      <h1 className="font-serif text-2xl font-bold">Reset Your GKK-FMIS Password</h1>
      {msg ? <p className="rounded-xl bg-[#E8F5E9] p-3 font-semibold" data-testid="forgot-message">{msg}</p> : (
        <form onSubmit={send} className="space-y-4">
          <Field label="Registered email address"><Input type="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" /></Field>
          {err && <p className="text-[#B91C1C] font-semibold">{err}</p>}
          <Btn type="submit" className="w-full" loading={busy} disabled={busy} data-testid="forgot-submit">Send Password Reset Link</Btn>
        </form>)}
      <p className="text-sm text-[#6E6259]" data-testid="google-note">If your account uses Google Sign-In, please continue using your Google account. If you have forgotten your Google password, recover it through Google's official account recovery service.</p>
      <Back />
    </Wrap>
  );
}

export function RegistrationStatus() {
  const [f, setF] = useState({ email: "", ref_no: "" });
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [sent, setSent] = useState(false);
  const check = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try { const { data } = await api.post("/registrations/status", f); setSt(data); } catch (x) { setErr(errMsg(x)); setSt(null); } finally { setBusy(false); }
  };
  const send = async () => {
    const fd = new FormData();
    fd.append("email", f.email); fd.append("ref_no", f.ref_no); fd.append("message", reply);
    Array.from(files).forEach((x) => fd.append("files", x));
    setBusy(true);
    try { await api.post("/registrations/reply", fd); setSent(true); check(); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Wrap>
      <h1 className="font-serif text-2xl font-bold">Check Registration Status</h1>
      <form onSubmit={check} className="space-y-4">
        <Field label="Email address used in your registration"><Input type="email" autoCapitalize="none" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="status-email-input" /></Field>
        <Field label="Reference number (e.g. REG-2026-000001)"><Input value={f.ref_no} onChange={(e) => setF({ ...f, ref_no: e.target.value })} required data-testid="status-ref-input" /></Field>
        <Btn type="submit" className="w-full" loading={busy} data-testid="status-submit">Check status</Btn>
      </form>
      {err && <p className="text-[#B91C1C] font-semibold" data-testid="status-error">{err}</p>}
      {st && <div className="rounded-xl bg-[#F3EDE2] p-4 space-y-1" data-testid="status-result">
        <p className="text-lg font-bold" data-testid="status-label">{st.label}</p><p className="text-sm">{st.position} · {st.gkk}{st.unit ? ` · ${st.unit}` : ""}</p>
        {st.message && <p className="text-sm"><b>Message from the Parish Office:</b> {st.message}</p>}</div>}
      {st?.status === "info_required" && !sent && <div className="space-y-3">
        <TextArea placeholder="Your answer / additional information" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="status-reply-input" />
        <input type="file" multiple accept="image/jpeg,image/png,application/pdf" onChange={(e) => setFiles(e.target.files)} data-testid="status-reply-files" />
        <Btn v="green" disabled={!reply.trim() || busy} onClick={send} data-testid="status-reply-submit">Send to the Parish Office</Btn></div>}
      <Back />
    </Wrap>
  );
}
